"use client";

import {useState, useRef, useEffect} from "react";
import Image from "next/image";
import VariantImage from "@/components/variants/variant-image";
import {Percent, ChevronDown, ChevronLeft, ChevronRight} from "lucide-react";
import {Variant} from "@/types";
import {FavoriteButton} from "@/components/favorites/favorite-button";
import {ShareButton} from "@/components/variants/share-button";
import {Button} from "@/shadcn/components/ui/button";

interface ProductImageGalleryProps {
    images: string[];
    title: string;
    discount: number;
    variants?: Variant[];
    currentVariant?: Variant;
}

// Mapping von Farbnamen zu CSS Hex-Codes
const colorMap: Record<string, string> = {
    'grau': '#6b7280', 'grey': '#6b7280', 'anthrazit': '#374151', 'dunkelgrau': '#4b5563',
    'hellgrau': '#9ca3af', 'silber': '#c0c0c0', 'blau': '#3b82f6', 'blue': '#3b82f6',
    'dunkelblau': '#1e40af', 'hellblau': '#93c5fd', 'navy': '#1e3a5f', 'petrol': '#0d9488',
    'türkis': '#14b8a6', 'grün': '#22c55e', 'green': '#22c55e', 'dunkelgrün': '#166534',
    'hellgrün': '#86efac', 'olive': '#84cc16', 'mint': '#a7f3d0', 'rot': '#ef4444',
    'red': '#ef4444', 'bordeaux': '#881337', 'weinrot': '#881337', 'rosa': '#f9a8d4',
    'pink': '#ec4899', 'gelb': '#eab308', 'yellow': '#eab308', 'orange': '#f97316',
    'terracotta': '#c2410c', 'curry': '#ca8a04', 'braun': '#78350f', 'brown': '#78350f',
    'beige': '#d4a574', 'sand': '#d1c4a0', 'taupe': '#87796f', 'cappuccino': '#a1887f',
    'cognac': '#8b4513', 'natur': '#d4b896', 'schwarz': '#171717', 'black': '#171717',
    'weiß': '#f5f5f5', 'weiss': '#f5f5f5', 'white': '#f5f5f5', 'creme': '#fffdd0',
    'ivory': '#fffff0', 'lila': '#a855f7', 'violett': '#8b5cf6', 'purple': '#a855f7',
    'gold': '#ffd700', 'bronze': '#cd7f32', 'kupfer': '#b87333',
    'bunt': 'linear-gradient(135deg, #f97316, #eab308, #22c55e, #3b82f6, #a855f7)',
    'multicolor': 'linear-gradient(135deg, #f97316, #eab308, #22c55e, #3b82f6, #a855f7)',
};

function getColorCode(colorName: string): string {
    const normalizedName = colorName.toLowerCase().trim();
    if (colorMap[normalizedName]) return colorMap[normalizedName];
    for (const [key, value] of Object.entries(colorMap)) {
        if (normalizedName.includes(key) || key.includes(normalizedName)) return value;
    }
    return '#9ca3af';
}

function getUniqueColors(variants: Variant[]): string[] {
    const colors = new Set<string>();
    variants.forEach(v => {
        if (v.color) {
            const colorValue = Array.isArray(v.color) ? v.color[0] : v.color;
            if (colorValue && colorValue !== 'none') colors.add(colorValue);
        }
    });
    return Array.from(colors).slice(0, 5);
}

export function ProductImageGallery({images, title, discount, variants = [], currentVariant}: ProductImageGalleryProps) {
    // Images are pre-processed by parent components following priority:
    // 1. clean images > 2. alt_image_url > 3. extracted from productserve > 4. productserve as-is > 5. thumbnail
    // This component just needs to handle the final fallback to placeholder if empty array is passed
    const displayImages = images.length > 0 ? images.slice(0, 5) : ['/img/furniture-sketch.svg'];
    const [selectedIndex, setSelectedIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    // Check if we have multiple images
    const hasMultipleImages = displayImages.length > 1;

    // Varianten-Farben berechnen
    const uniqueColors = getUniqueColors(variants);
    const hasVariants = variants.length > 0;

    const handleVariantsClick = () => {
        const element = document.getElementById('weitere-varianten');
        if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    };

    // Scroll to specific image index
    const scrollToIndex = (index: number) => {
        const container = scrollContainerRef.current;
        if (container) {
            const scrollAmount = index * container.clientWidth;
            container.scrollTo({left: scrollAmount, behavior: 'smooth'});
        }
        setSelectedIndex(index);
    };

    // Navigate to next/prev image
    const scrollToImage = (direction: 'next' | 'prev') => {
        const newIndex = direction === 'next'
            ? Math.min(selectedIndex + 1, displayImages.length - 1)
            : Math.max(selectedIndex - 1, 0);
        scrollToIndex(newIndex);
    };

    // Update selected index based on scroll position
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !hasMultipleImages) return;

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const itemWidth = container.clientWidth;
            const newIndex = Math.round(scrollLeft / itemWidth);
            if (newIndex !== selectedIndex && newIndex >= 0 && newIndex < displayImages.length) {
                setSelectedIndex(newIndex);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [selectedIndex, displayImages.length, hasMultipleImages]);

    return (
        <div className="bg-white rounded-xl p-4 lg:sticky lg:top-4">
            {/* Gallery Layout - Thumbnails on left when multiple images */}
            <div className={`flex ${hasMultipleImages ? 'gap-3' : ''}`}>
                {/* Vertical Thumbnails - Only show when multiple images */}
                {hasMultipleImages && (
                    <div className="hidden sm:flex flex-col gap-2 flex-shrink-0">
                        {displayImages.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => scrollToIndex(i)}
                                aria-label={`${title} Bild ${i + 1} anzeigen`}
                                aria-pressed={i === selectedIndex}
                                className={`w-14 h-14 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all ${
                                    i === selectedIndex
                                        ? 'border-amber-500 ring-2 ring-amber-500/20'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <Image
                                    src={img}
                                    alt={`${title} Bild ${i + 1}`}
                                    width={56}
                                    height={56}
                                    className="w-full h-full object-contain bg-gray-50"
                                />
                            </button>
                        ))}
                    </div>
                )}

                {/* Main Image Container */}
                <div className="flex-1 min-w-0">
                    <div className="relative aspect-square bg-gray-50 rounded-lg overflow-hidden group">
                        {hasMultipleImages ? (
                            // Scrollable gallery for multiple images
                            <div
                                ref={scrollContainerRef}
                                className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth h-full w-full scrollbar-hide"
                                style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}
                            >
                                {displayImages.map((img, i) => (
                                    <div key={i} className="w-full h-full flex-shrink-0 snap-center relative">
                                        <VariantImage
                                            src={img}
                                            alt={`${title} - Bild ${i + 1}`}
                                            fill
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 450px"
                                            className="object-contain"
                                            priority={i === 0}
                                            fetchPriority={i === 0 ? "high" : "auto"}
                                            blurDataURL={i === 0 ? currentVariant?.image_blur : undefined}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            // Single image - no scroll needed
                            <VariantImage
                                src={displayImages[0]}
                                alt={title}
                                fill
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 450px"
                                className="object-contain"
                                priority
                                fetchPriority="high"
                                blurDataURL={currentVariant?.image_blur}
                            />
                        )}

                        {/* Navigation Arrows - Only for multiple images */}
                        {hasMultipleImages && (
                            <>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    aria-label="Vorheriges Bild"
                                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white shadow-md h-8 w-8"
                                    onClick={() => scrollToImage('prev')}
                                    disabled={selectedIndex === 0}
                                >
                                    <ChevronLeft className="h-4 w-4" aria-hidden="true"/>
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    aria-label="Nächstes Bild"
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white shadow-md h-8 w-8"
                                    onClick={() => scrollToImage('next')}
                                    disabled={selectedIndex === displayImages.length - 1}
                                >
                                    <ChevronRight className="h-4 w-4" aria-hidden="true"/>
                                </Button>
                            </>
                        )}

                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                            {discount > 0 && (
                                <span className="bg-rose-600 text-white px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                                    <Percent className="w-3 h-3"/>
                                    -{discount.toFixed(0)}%
                                </span>
                            )}
                        </div>

                        {/* Actions */}
                        {currentVariant && (
                            <div className="absolute top-3 right-3 flex gap-1.5 z-10">
                                <FavoriteButton variant={currentVariant} size="sm"/>
                                <ShareButton variant={currentVariant} size="sm"/>
                            </div>
                        )}

                        {/* Dot indicators - Only for multiple images on mobile */}
                        {hasMultipleImages && (
                            <div className="sm:hidden absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10">
                                {displayImages.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => scrollToIndex(i)}
                                        aria-label={`Bild ${i + 1} anzeigen`}
                                        className={`w-2 h-2 rounded-full transition-all ${
                                            i === selectedIndex
                                                ? 'bg-amber-500 w-4'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile Thumbnails - Only show on mobile when multiple images */}
                    {hasMultipleImages && (
                        <div className="flex sm:hidden gap-2 overflow-x-auto pb-1 mt-3">
                            {displayImages.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => scrollToIndex(i)}
                                    aria-label={`${title} Bild ${i + 1} anzeigen`}
                                    aria-pressed={i === selectedIndex}
                                    className={`w-14 h-14 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all ${
                                        i === selectedIndex
                                            ? 'border-amber-500 ring-2 ring-amber-500/20'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Image
                                        src={img}
                                        alt={`${title} Bild ${i + 1}`}
                                        width={56}
                                        height={56}
                                        className="w-full h-full object-contain bg-gray-50"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Variants Indicator - integrated in gallery */}
            {hasVariants && (
                <button
                    onClick={handleVariantsClick}
                    className="w-full flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-100 text-slate-600 hover:text-slate-900 transition-colors group"
                >
                    <div className="flex items-center gap-3">
                        {/* Farb-Punkte */}
                        <div className="flex -space-x-1">
                            {uniqueColors.length > 0 ? (
                                uniqueColors.map((color, i) => {
                                    const colorCode = getColorCode(color);
                                    const isGradient = colorCode.includes('gradient');
                                    return (
                                        <div
                                            key={i}
                                            className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                                            style={{
                                                background: isGradient ? colorCode : undefined,
                                                backgroundColor: !isGradient ? colorCode : undefined,
                                            }}
                                            title={color}
                                        />
                                    );
                                })
                            ) : (
                                Array.from({length: Math.min(variants.length, 3)}).map((_, i) => (
                                    <div
                                        key={i}
                                        className="w-5 h-5 rounded-full border-2 border-white shadow-sm bg-gray-300"
                                    />
                                ))
                            )}
                            {variants.length > 5 && (
                                <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm bg-gray-200 flex items-center justify-center text-[8px] font-bold text-slate-600">
                                    +{variants.length - 5}
                                </div>
                            )}
                        </div>
                        <span className="text-sm font-medium">
                            {variants.length} weitere {variants.length === 1 ? 'Variante' : 'Varianten'}
                        </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors"/>
                </button>
            )}
        </div>
    );
}
